import { useCallback } from 'react';
import { useBearAuth } from '@bear-auth/react';

import { NotificationStatus, useUpdateNotification, type NotificationId } from '@/modules/api/codegen';

import { useUserNotification } from './useUserNotification';

export function useNotification(notificationId: NotificationId) {
    const {
        actions: { refetchAuthInfo },
    } = useBearAuth();
    const { isLoading, notification } = useUserNotification(notificationId);
    const { mutateAsync: updateNotification, ...updateNotificationMutation } = useUpdateNotification();

    const update = useCallback(
        async (status: NotificationStatus) => {
            try {
                await updateNotification({
                    pathParams: { notificationId },
                    data: { status },
                });
                await refetchAuthInfo?.();
            } catch (error) {
                console.error(error);
            }
        },
        [notificationId, refetchAuthInfo, updateNotification],
    );

    const rejectNotification = useCallback(() => update(NotificationStatus.rejected), [update]);

    const resolveNotification = useCallback(() => update(NotificationStatus.resolved), [update]);

    return {
        notification,
        isLoading,
        update: {
            ...updateNotificationMutation,
            rejectNotification,
            resolveNotification,
        },
    } as const;
}
